/**
 * hooks/useInsightsData.js — extracted from pages/Insights.js in v3.4.7
 *
 * Loads the last 12 months of transactions plus every input needed by
 * the authoritative Clarity Score computation (`lib/clarityScore.js`).
 * Extracted so components outside the Insights page (Life Profile card,
 * future analytics widgets) can reuse the same data-fetch without
 * duplicating the Supabase queries.
 *
 * Waits for the CurrencyContext to be ready before enriching txns —
 * enriching with an identity rate and then not re-enriching after
 * context loads is exactly the race that produced
 * "MONTHLY SPENDING ₹70,985.78" on an AED profile.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

export function useInsightsData(userId, currencyCtx) {
  const [txns, setTxns] = useState([]);
  const [goals, setGoals] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const ctxReady = currencyCtx.isReady;
  useEffect(() => {
    let c = false;
    if (!ctxReady) return () => {};
    (async () => {
      try {
        const start = new Date(); start.setMonth(start.getMonth() - 12); start.setDate(1);
        const startISO = start.toISOString().slice(0, 10);
        const [txnR, goalR, recR, onbR, whR, liabR] = await Promise.all([
          supabase.from('transactions')
            .select('id, txn_date, amount, category, type, description, source, account_id')
            .gte('txn_date', startISO)
            .order('txn_date', { ascending: false })
            .limit(5000),
          supabase.from('goals').select('id, title, current_amount, target_amount, status, target_date, updated_at'),
          supabase.from('recurring_expenses').select('id, name, amount, category, frequency, active'),
          supabase.from('onboarding_profiles').select('*').maybeSingle(),
          supabase.from('wealth_holdings').select('id, kind, value, currency'),
          supabase.from('liabilities').select('id, kind, outstanding, currency'),
        ]);
        if (!c) {
          const enriched = (txnR.data || []).map((t) => {
            const cur = currencyCtx.currencyOfAccount(t.account_id);
            return { ...t, _currency: cur, _baseAmount: currencyCtx.toBase(Number(t.amount || 0), cur) };
          });
          setTxns(enriched);
          setGoals(goalR.data || []);
          setRecurring(recR.data || []);
          setOnboarding(onbR.data || null);
          setHoldings(whR.data || []);
          setLiabilities(liabR.data || []);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[insights] load failed:', e);
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, [userId, ctxReady]);  // eslint-disable-line react-hooks/exhaustive-deps
  return { txns, goals, recurring, onboarding, holdings, liabilities, loading };
}

export default useInsightsData;
